import ExcelJS from 'exceljs'
import type {
  ScheduleEffectivenessResponse,
  ScheduleEffectivenessRow,
} from '../types/reports.types'

const COLORS = {
  headerBg: 'F3F4F6',
  deptBg: 'E5E7EB',
  blue: 'DBEAFE',
  green: 'DCFCE7',
  emerald: 'D1FAE5',
  red: 'FEE2E2',
  amber: 'FEF3C7',
  greenText: '16A34A',
  redText: 'DC2626',
  amberText: 'B45309',
  blueText: '1D4ED8',
}

function thinBorder(): Partial<ExcelJS.Borders> {
  return {
    top: { style: 'thin', color: { argb: 'D1D5DB' } },
    left: { style: 'thin', color: { argb: 'D1D5DB' } },
    bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
    right: { style: 'thin', color: { argb: 'D1D5DB' } },
  }
}

function solidFill(color: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb: color } }
}

function pctText(value: number | null): string {
  return value === null ? '—' : `${value}%`
}

function rowValues(row: ScheduleEffectivenessRow): (string | number)[] {
  return [
    row.employees,
    row.base_plan,
    row.exceptions_total,
    pctText(row.change_pct),
    row.added,
    row.removed,
    row.time_changed,
    row.replacements,
    row.actual,
    row.no_shows,
    pctText(row.adherence_pct),
  ]
}

export async function exportScheduleEffectivenessToExcel(
  report: ScheduleEffectivenessResponse,
): Promise<void> {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'TCO Admin'
  workbook.created = new Date()

  const ws = workbook.addWorksheet('Эффективность графиков', {
    views: [{ state: 'frozen', ySplit: 4 }],
  })

  // Title
  const title = ws.addRow([
    `Эффективность графиков — ${report.from} … ${report.to}`,
  ])
  ws.mergeCells(1, 1, 1, 12)
  title.font = { size: 14, bold: true }
  title.height = 26

  // Summary
  const summaryRow = ws.addRow([
    `Стабильность: ${pctText(report.summary.stability_pct)}`,
    '',
    `Исключений: ${report.summary.exceptions_total}`,
    '',
    `Соблюдение: ${pctText(report.summary.adherence_pct)}`,
    '',
    `Холостые смены: ${report.summary.no_shows}`,
  ])
  summaryRow.font = { size: 10, bold: true }
  ws.addRow([])

  // Header
  const header = ws.addRow([
    'Подразделение / Должность',
    'Сотр.',
    'План (база)',
    'Исключ.',
    '% изм.',
    '+Смена',
    '−Смена',
    'Время',
    'Замена',
    'Факт',
    'Неявки',
    '% соблюд.',
  ])
  header.font = { bold: true, size: 9 }
  header.height = 20
  header.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    cell.border = thinBorder()
    cell.fill = solidFill(COLORS.headerBg)
  })

  // Department + position rows
  for (const dept of report.departments) {
    const deptLabel = dept.organization_name
      ? `${dept.department_name} (${dept.organization_name})`
      : dept.department_name
    const deptRow = ws.addRow([deptLabel, ...rowValues(dept)])
    deptRow.font = { bold: true, size: 10 }
    deptRow.eachCell((cell, col) => {
      cell.border = thinBorder()
      cell.fill = solidFill(COLORS.deptBg)
      cell.alignment = { horizontal: col === 1 ? 'left' : 'center', vertical: 'middle' }
    })

    for (const pos of dept.positions) {
      const posRow = ws.addRow([`    ${pos.position_name}`, ...rowValues(pos)])
      posRow.font = { size: 9 }
      posRow.eachCell((cell, col) => {
        cell.border = thinBorder()
        cell.alignment = { horizontal: col === 1 ? 'left' : 'center', vertical: 'middle' }
        if (col === 6) cell.font = { size: 9, color: { argb: COLORS.greenText } }
        if (col === 7 || col === 11) cell.font = { size: 9, color: { argb: COLORS.redText } }
        if (col === 8) cell.font = { size: 9, color: { argb: COLORS.blueText } }
        if (col === 9) cell.font = { size: 9, color: { argb: COLORS.amberText } }
      })
    }
  }

  // Authors block
  ws.addRow([])
  const authorsTitle = ws.addRow(['Кто меняет график'])
  authorsTitle.font = { size: 12, bold: true }
  const authorsHeader = ws.addRow([
    'Пользователь',
    'Исключений',
    '+Смена',
    '−Смена',
    'Время',
    'Замена',
    'Подразделения',
  ])
  authorsHeader.font = { bold: true, size: 9 }
  authorsHeader.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.border = thinBorder()
    cell.fill = solidFill(COLORS.headerBg)
  })
  for (const author of report.authors) {
    const row = ws.addRow([
      author.user_name,
      author.exceptions_total,
      author.added,
      author.removed,
      author.time_changed,
      author.replacements,
      author.departments.join(', '),
    ])
    row.font = { size: 9 }
    row.eachCell((cell, col) => {
      cell.border = thinBorder()
      cell.alignment = {
        horizontal: col === 1 || col === 7 ? 'left' : 'center',
        vertical: 'middle',
      }
    })
  }

  // Column widths
  ws.getColumn(1).width = 40
  for (let i = 2; i <= 12; i++) ws.getColumn(i).width = 10
  ws.getColumn(12).width = 11

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const filename = `Эффективность_графиков_${report.from}_${report.to}.xlsx`
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}
